import { useRef, useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  Alert,
  AlertIcon,
  Button,
  Input,
  Select,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  SimpleGrid,
  IconButton,
  HStack,
  Badge,
  Switch,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
} from "@chakra-ui/react";
import PageLoader from "./components/PageLoader";
import { EditIcon, AddIcon, RepeatIcon } from "@chakra-ui/icons";
import api from "./api";

const QUEST_KINDS = [
  "DAILY_CHECKIN",
  "SOCIAL_TWITTER",
  "SOCIAL_TELEGRAM",
  "TIME_ATTACK",
  "LEVEL_UP",
  "STAKING",
];

const KIND_COLORS: Record<string, string> = {
  DAILY_CHECKIN: "green",
  SOCIAL_TWITTER: "blue",
  SOCIAL_TELEGRAM: "blue",
  TIME_ATTACK: "orange",
  LEVEL_UP: "red",
  STAKING: "teal",
};

interface Quest {
  _id?: string;
  key: string;
  kind: string;
  title: string;
  description: string;
  reward: {
    xp: number;
    raffleTickets: number;
    letters: string[];
  };
  cooldownSec: number;
  startsAt?: string;
  endsAt?: string;
  seasonId?: string;
  target?: {
    volumeUsd?: number;
    pnlUsd?: number;
    tweetUrl?: string;
    telegramUrl?: string;
  };
  enabled: boolean;
}

interface Kpis {
  rangeDays: number;
  questClaims: number;
  raffleSpins: number;
  leaderboardViews: number;
  uniqueClaimers: number;
  totalXpAwarded: number;
}

const emptyQuest: Quest = {
  key: "",
  kind: "DAILY_CHECKIN",
  title: "",
  description: "",
  reward: { xp: 0, raffleTickets: 0, letters: [] },
  cooldownSec: 0,
  target: {},
  enabled: true,
};

function Quests() {
  const [quests, setQuests] = useState<Quest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<Quest>({ ...emptyQuest });
  const [lettersInput, setLettersInput] = useState("");
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [kpiDays, setKpiDays] = useState(30);
  const [seeding, setSeeding] = useState(false);
  const [isRolloverOpen, setIsRolloverOpen] = useState(false);
  const [rollingOver, setRollingOver] = useState(false);
  const rolloverCancelRef = useRef<HTMLButtonElement>(null);
  const [questBlocked, setQuestBlocked] = useState(false);
  const [blockToggling, setBlockToggling] = useState(false);
  const [raffleEnabled, setRaffleEnabled] = useState(false);
  const [raffleToggling, setRaffleToggling] = useState(false);

  const fetchQuests = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/api/arena/quests");
      setQuests(res?.data?.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Error loading quests");
    } finally {
      setLoading(false);
    }
  };

  const fetchKpis = async () => {
    try {
      const res = await api.get(`/api/arena/kpis?days=${kpiDays}`);
      setKpis(res?.data?.data || null);
    } catch {
      /* ignore */
    }
  };

  const fetchBlockStatus = async () => {
    try {
      const res = await api.get("/api/arena/block-status");
      setQuestBlocked(res?.data?.data?.blocked ?? false);
      setRaffleEnabled(res?.data?.data?.raffleEnabled ?? false);
    } catch { /* ignore */ }
  };

  const handleToggleRaffle = async () => {
    setRaffleToggling(true);
    try {
      const res = await api.post("/api/arena/toggle-raffle");
      const enabled = res?.data?.data?.raffleEnabled ?? false;
      setRaffleEnabled(enabled);
      toast.success(enabled ? "Raffle wheel enabled" : "Raffle wheel disabled");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to toggle raffle");
    } finally {
      setRaffleToggling(false);
    }
  };

  const handleToggleBlock = async () => {
    setBlockToggling(true);
    try {
      const res = await api.post("/api/arena/toggle-block");
      const blocked = res?.data?.data?.blocked ?? false;
      setQuestBlocked(blocked);
      toast.success(blocked ? "Quest page blocked" : "Quest page unblocked");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to toggle block");
    } finally {
      setBlockToggling(false);
    }
  };

  useEffect(() => {
    fetchQuests();
    fetchKpis();
    fetchBlockStatus();
  }, []);

  useEffect(() => {
    fetchKpis();
  }, [kpiDays]);

  const handleAdd = () => {
    setIsEditing(false);
    setForm({ ...emptyQuest });
    setLettersInput("");
    setIsFormOpen(true);
  };

  const handleEdit = (q: Quest) => {
    setIsEditing(true);
    setForm({ ...q });
    setLettersInput((q.reward?.letters || []).join(", "));
    setIsFormOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        reward: {
          ...form.reward,
          letters: lettersInput
            .split(",")
            .map((l) => l.trim().toUpperCase())
            .filter(Boolean),
        },
      };
      await api.post("/api/arena/quests", payload);
      toast.success(isEditing ? "Quest updated" : "Quest created");
      setIsFormOpen(false);
      fetchQuests();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save quest");
    }
  };

  const handleToggle = async (q: Quest) => {
    try {
      await api.post("/api/arena/quests", {
        ...q,
        enabled: !q.enabled,
      });
      toast.success(`Quest ${!q.enabled ? "enabled" : "disabled"}`);
      fetchQuests();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to toggle quest");
    }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const res = await api.post("/api/arena/seed-defaults");
      toast.success(`Seeded ${res?.data?.seeded || 0} default quests`);
      fetchQuests();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  const handleRolloverSeason = async () => {
    setRollingOver(true);
    try {
      const res = await api.post("/api/arena/season/rollover", {});
      toast.success(`New season: ${res?.data?.data?.seasonId}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Rollover failed");
    } finally {
      setRollingOver(false);
      setIsRolloverOpen(false);
    }
  };

  if (loading) return <PageLoader />;

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
          <Heading size="lg" color="white">
            Quest Management
          </Heading>
          <HStack spacing={3} flexWrap="wrap">
            <Button
              size="sm"
              colorScheme={questBlocked ? "green" : "yellow"}
              variant={questBlocked ? "solid" : "outline"}
              onClick={handleToggleBlock}
              isLoading={blockToggling}
            >
              {questBlocked ? "Unblock Quests" : "Block Quests"}
            </Button>
            <Button
              size="sm"
              colorScheme={raffleEnabled ? "yellow" : "green"}
              variant={raffleEnabled ? "outline" : "solid"}
              onClick={handleToggleRaffle}
              isLoading={raffleToggling}
            >
              {raffleEnabled ? "Disable Raffle" : "Enable Raffle"}
            </Button>
            <Button
              leftIcon={<RepeatIcon />}
              size="sm"
              colorScheme="orange"
              variant="outline"
              onClick={handleSeedDefaults}
              isLoading={seeding}
            >
              Seed Defaults
            </Button>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              onClick={() => setIsRolloverOpen(true)}
            >
              Rollover Season
            </Button>
            <Button
              leftIcon={<AddIcon />}
              size="sm"
              colorScheme="blue"
              onClick={handleAdd}
            >
              Add Quest
            </Button>
          </HStack>
        </Box>

        {error && (
          <Alert status="error" borderRadius="md" bg="red.900" color="red.200">
            <AlertIcon />
            {error}
          </Alert>
        )}

        <Tabs variant="soft-rounded" colorScheme="blue">
          <TabList>
            <Tab color="gray.300" _selected={{ color: "white", bg: "brand.600" }}>
              Quests
            </Tab>
            <Tab color="gray.300" _selected={{ color: "white", bg: "brand.600" }}>
              KPIs
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <Box overflowX="auto" borderRadius="lg" border="1px solid" borderColor="gray.700" bg="gray.800">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color="gray.400">Key</Th>
                      <Th color="gray.400">Kind</Th>
                      <Th color="gray.400">Title</Th>
                      <Th color="gray.400" isNumeric>XP</Th>
                      <Th color="gray.400" isNumeric>Tickets</Th>
                      <Th color="gray.400">Letters</Th>
                      <Th color="gray.400" isNumeric>Cooldown</Th>
                      <Th color="gray.400">Enabled</Th>
                      <Th color="gray.400">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {quests.length === 0 ? (
                      <Tr>
                        <Td colSpan={9} textAlign="center" color="gray.500" py={8}>
                          No quests found. Click "Seed Defaults" to create starter quests.
                        </Td>
                      </Tr>
                    ) : (
                      quests.map((q) => (
                        <Tr key={q.key} _hover={{ bg: "gray.750" }}>
                          <Td color="gray.200" fontFamily="mono" fontSize="xs">
                            {q.key}
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={KIND_COLORS[q.kind] || "gray"}
                              variant="subtle"
                              fontSize="xs"
                            >
                              {q.kind.replace(/_/g, " ")}
                            </Badge>
                          </Td>
                          <Td color="white" fontWeight="500" maxW="200px" isTruncated>
                            {q.title}
                          </Td>
                          <Td isNumeric color="green.300" fontWeight="600">
                            {q.reward?.xp || 0}
                          </Td>
                          <Td isNumeric color="orange.300">
                            {q.reward?.raffleTickets || 0}
                          </Td>
                          <Td color="blue.300" fontFamily="mono" fontSize="xs">
                            {(q.reward?.letters || []).join(", ") || "—"}
                          </Td>
                          <Td isNumeric color="gray.300" fontSize="xs">
                            {q.cooldownSec ? `${Math.round(q.cooldownSec / 3600)}h` : "—"}
                          </Td>
                          <Td>
                            <Switch
                              colorScheme="green"
                              size="sm"
                              isChecked={q.enabled}
                              onChange={() => handleToggle(q)}
                            />
                          </Td>
                          <Td>
                            <IconButton
                              icon={<EditIcon />}
                              aria-label="Edit"
                              size="xs"
                              variant="ghost"
                              color="blue.300"
                              onClick={() => handleEdit(q)}
                            />
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </TabPanel>

            <TabPanel px={0}>
              <VStack spacing={6} align="stretch">
                <HStack>
                  <Select
                    size="sm"
                    w="160px"
                    bg="gray.800"
                    borderColor="gray.600"
                    color="white"
                    value={kpiDays}
                    onChange={(e) => setKpiDays(Number(e.target.value))}
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                  </Select>
                  <Button size="sm" variant="ghost" color="gray.400" onClick={fetchKpis}>
                    <RepeatIcon />
                  </Button>
                </HStack>
                {kpis ? (
                  <StatGroup gap={4} flexWrap="wrap">
                    <KpiCard label="Quest Claims" value={kpis.questClaims} />
                    <KpiCard label="Raffle Spins" value={kpis.raffleSpins} />
                    <KpiCard label="Unique Claimers" value={kpis.uniqueClaimers} />
                    <KpiCard label="Total XP Awarded" value={kpis.totalXpAwarded} />
                    <KpiCard label="Leaderboard Views" value={kpis.leaderboardViews} />
                  </StatGroup>
                ) : (
                  <Text color="gray.500">Loading KPIs…</Text>
                )}
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      {/* Add/Edit Quest Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent bg="gray.800" borderColor="gray.700" border="1px solid">
          <ModalHeader color="white">
            {isEditing ? "Edit Quest" : "Add Quest"}
          </ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody>
            <SimpleGrid columns={2} spacing={4}>
              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">Key</FormLabel>
                <Input
                  size="sm"
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  value={form.key}
                  onChange={(e) => setForm({ ...form, key: e.target.value })}
                  isDisabled={isEditing}
                  placeholder="e.g. daily-checkin"
                />
              </FormControl>
              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">Kind</FormLabel>
                <Select
                  size="sm"
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                >
                  {QUEST_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl gridColumn="span 2">
                <FormLabel color="gray.300" fontSize="sm">Title</FormLabel>
                <Input
                  size="sm"
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Quest title"
                />
              </FormControl>
              <FormControl gridColumn="span 2">
                <FormLabel color="gray.300" fontSize="sm">Description</FormLabel>
                <Textarea
                  size="sm"
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                />
              </FormControl>
              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">XP Reward</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  value={form.reward.xp}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      reward: { ...form.reward, xp: Number(e.target.value) },
                    })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">Raffle Tickets</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  value={form.reward.raffleTickets}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      reward: { ...form.reward, raffleTickets: Number(e.target.value) },
                    })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">Letters (comma-separated)</FormLabel>
                <Input
                  size="sm"
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  value={lettersInput}
                  onChange={(e) => setLettersInput(e.target.value)}
                  placeholder="Q, U, E"
                />
              </FormControl>
              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">Cooldown (seconds)</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  value={form.cooldownSec}
                  onChange={(e) => setForm({ ...form, cooldownSec: Number(e.target.value) })}
                  placeholder="86400 = 24h"
                />
              </FormControl>

              {(form.kind === "SOCIAL_TWITTER" || form.kind === "SOCIAL_TELEGRAM") && (
                <>
                  {form.kind === "SOCIAL_TWITTER" && (
                    <FormControl gridColumn="span 2">
                      <FormLabel color="gray.300" fontSize="sm">Tweet URL</FormLabel>
                      <Input
                        size="sm"
                        bg="gray.900"
                        borderColor="gray.600"
                        color="white"
                        value={form.target?.tweetUrl || ""}
                        onChange={(e) =>
                          setForm({ ...form, target: { ...form.target, tweetUrl: e.target.value } })
                        }
                        placeholder="https://x.com/PerpXdotAi/status/..."
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Accepts both x.com and twitter.com URLs. The tweet ID is auto-extracted for verification.
                      </Text>
                    </FormControl>
                  )}
                  {form.kind === "SOCIAL_TELEGRAM" && (
                    <FormControl gridColumn="span 2">
                      <FormLabel color="gray.300" fontSize="sm">Telegram Chat ID</FormLabel>
                      <Input
                        size="sm"
                        bg="gray.900"
                        borderColor="gray.600"
                        color="white"
                        value={form.target?.telegramUrl || ""}
                        onChange={(e) =>
                          setForm({ ...form, target: { ...form.target, telegramUrl: e.target.value } })
                        }
                        placeholder="-1002567342161"
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Numeric supergroup/channel ID (e.g. -1002567342161) or @username for public channels. The bot must be a member (admin recommended) of this chat.
                      </Text>
                    </FormControl>
                  )}
                </>
              )}

              {(form.kind === "TIME_ATTACK" || form.kind === "LEVEL_UP") && (
                <>
                  <FormControl>
                    <FormLabel color="gray.300" fontSize="sm">Volume Target ($)</FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      bg="gray.900"
                      borderColor="gray.600"
                      color="white"
                      value={form.target?.volumeUsd || ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          target: { ...form.target, volumeUsd: Number(e.target.value) || undefined },
                        })
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel color="gray.300" fontSize="sm">PnL Target ($)</FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      bg="gray.900"
                      borderColor="gray.600"
                      color="white"
                      value={form.target?.pnlUsd || ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          target: { ...form.target, pnlUsd: Number(e.target.value) || undefined },
                        })
                      }
                    />
                  </FormControl>
                </>
              )}

              <FormControl display="flex" alignItems="center">
                <FormLabel color="gray.300" fontSize="sm" mb={0}>
                  Enabled
                </FormLabel>
                <Switch
                  colorScheme="green"
                  isChecked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                />
              </FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              color="gray.400"
              mr={3}
              onClick={() => setIsFormOpen(false)}
            >
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSave}>
              {isEditing ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Rollover Season Confirmation */}
      <AlertDialog
        isOpen={isRolloverOpen}
        leastDestructiveRef={rolloverCancelRef}
        onClose={() => setIsRolloverOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800" borderColor="gray.700" border="1px solid">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="white">
              Rollover Season
            </AlertDialogHeader>
            <AlertDialogBody color="gray.300">
              This will deactivate the current season and create a new one. All user levels will
              start fresh. This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={rolloverCancelRef}
                variant="ghost"
                color="gray.400"
                onClick={() => setIsRolloverOpen(false)}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleRolloverSeason}
                isLoading={rollingOver}
                ml={3}
              >
                Rollover
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
}

function KpiCard({ label, value }: { label: string; value: number }) {
  return (
    <Stat
      bg="gray.800"
      border="1px solid"
      borderColor="gray.700"
      borderRadius="lg"
      p={4}
      minW="150px"
    >
      <StatLabel color="gray.400" fontSize="xs">
        {label}
      </StatLabel>
      <StatNumber color="white" fontSize="2xl">
        {value.toLocaleString()}
      </StatNumber>
    </Stat>
  );
}

export default Quests;
